import { useContext, createContext, useEffect, useState, useMemo } from 'react';
import { OrgUserDTO, OrgUserDTOFromJSON, TeamDTO } from '@atlassiansox/phobos-client-ts';
import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/services';
import { querySpec } from '@/services/api-service/api-queries';
import { TOME_ATLASSIAN_ORG_ID } from '@/constants';

import { useFetchErrorController } from '../components/primitive/error-boundary/hooks';

import useAuthController from './auth-controller';

type OrgUserContextType = OrgUserDTO & { isLoading: boolean };

const defaultState = {
  username: '',
  orgId: '',
  orgName: '',
  teams: [] as TeamDTO[],
  isLoading: true,
};

export const OrgUserContext = createContext<OrgUserContextType>(defaultState);

export function useOrgUserService() {
  const [orgUser, setOrgUser] = useState(defaultState);

  const { onError } = useFetchErrorController();
  const { username } = useAuthController();

  const isLocal = __DEPLOY_ENV__ === 'local';

  const { queryKey, path } = querySpec('org.user.{username}', { username });
  const { data: rawData, isInitialLoading: isLoading } = useQuery(queryKey, () => apiFetch(path), {
    onError,
    enabled: !isLocal && !!username,
  });
  const dataOrgUser: OrgUserDTO = useMemo(() => OrgUserDTOFromJSON(rawData || {}), [rawData]);

  useEffect(() => {
    if (isLocal) {
      setOrgUser({ ...defaultState, orgId: TOME_ATLASSIAN_ORG_ID, isLoading: false });
      return;
    }

    if (isLoading) return;

    const teams: TeamDTO[] = dataOrgUser.teams || [];
    setOrgUser({ ...dataOrgUser, teams, isLoading: false });
  }, [dataOrgUser, isLoading, isLocal]);

  return orgUser;
}

function useOrgUser() {
  return useContext(OrgUserContext);
}

export default useOrgUser;
